import { T } from '@tolgee/react';
import { FunctionComponent, useCallback, useState } from 'react';
import styled from 'styled-components';

import theme from '../../../assets/theme';
import { usePaywallCtaTracking } from '../../../hooks/usePaywallCtaTracking';
import {
  ArrowIcon,
  CheckIcon,
  EmailForm,
  LinkButton,
  ModalBody,
  ModalShell,
  OrangeBtn,
  OrDivider,
  StickerHeader,
} from './primitives';
import { isInternational } from './stateSpecificStats';
import {
  CURRICULUM_TYPEFORM_URL,
  INTEREST_FORM_URL,
  LEARN_MORE_URL,
  PRICING_URL,
  QUOTES_URL,
} from './urls';

interface Props {
  visible: boolean;
  onClose: () => void;
  country: string | undefined;
  /** Where the modal was opened from, for CTA-click analytics. */
  source?: string;
  /** Optional `usePaywallPrompt` screen label, threaded into CTA event payloads. */
  screen?: string;
  onEmailSubmit?: (email: string) => void;
  /**
   * Which variant of the gate to render.
   * - `'default'` — the standard "this feature requires the school plan" gate,
   *   aimed at a classroom teacher.
   * - `'curriculum-addon'` — for users *already* on the school plan (admins /
   *   school-plan members): curriculum alignment is a paid add-on on top of the
   *   plan, so the copy asks them to request it rather than to buy a plan they
   *   already have.
   * - `'quote'` — for administrators / buyers (school or district admin,
   *   purchasing manager) who are *not* on a plan yet: buyer framing that asks
   *   them to request a quote for their school or district.
   * All three share the same lead-capture CTAs (request form + email) so sales
   * can follow up.
   */
  mode?: 'default' | 'curriculum-addon' | 'quote';
  /**
   * Optional per-curriculum request-page URL. When set, it becomes the primary
   * CTA destination for *every* variant — the default (teacher) gate included —
   * so a teacher on a listed curriculum lands on that curriculum's request page
   * instead of the generic interest form. When omitted, each mode uses its own
   * generic fallback: the school-plan interest form (default), the shared
   * curriculum lead Typeform (add-on), or the /quotes page (quote).
   */
  curriculumRequestUrl?: string;
}

const Heading = styled.h3`
  font-size: 15px;
  font-weight: 900;
  margin: 0 0 12px;
  font-family: 'Nunito', sans-serif;
  color: ${theme.colors.black};
  letter-spacing: -0.01em;
`;

const IncludedPanel = styled.div`
  margin-top: 16px;
  background: ${theme.colors.paleBeige};
  border: 1px solid ${theme.colors.beigeDark};
  border-radius: 10px;
  padding: 12px 14px;
`;

const PanelLabel = styled.div`
  font-size: 10.5px;
  font-weight: 900;
  color: rgba(40, 47, 51, 0.7);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 8px;
`;

const IncludedRow = styled.div<{ $last?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
  border-bottom: ${p => (p.$last ? 'none' : `1px dashed ${theme.colors.beigeDark}`)};
`;

const CheckBubble = styled.div`
  flex: none;
  width: 20px;
  height: 20px;
  border-radius: 999px;
  background: ${theme.colors.successBg};
  color: ${theme.colors.successText};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const IncludedText = styled.div`
  font-size: 12.5px;
  color: ${theme.colors.black};
  font-weight: 700;
  font-family: 'Nunito', sans-serif;
`;

const Centered = styled.div`
  text-align: center;
  margin-top: 12px;
`;

const ThanksBody = styled.div`
  padding: 30px 26px 26px;
  text-align: center;
`;

const ThanksSticker = styled.img`
  width: 80px;
  height: 80px;
  margin: 0 auto 12px;
  display: block;
`;

const ThanksHeading = styled.h2`
  font-size: 22px;
  font-weight: 900;
  margin: 0 0 8px;
  letter-spacing: -0.01em;
  font-family: 'Nunito', sans-serif;
`;

const ThanksText = styled.p`
  font-size: 13px;
  color: rgba(40, 47, 51, 0.7);
  margin: 0 0 18px;
  line-height: 1.55;
  font-weight: 700;
  font-family: 'Nunito', sans-serif;

  strong {
    color: #282f33;
    font-weight: 900;
  }
`;

export const PremiumFeatureGateModal: FunctionComponent<Props> = ({
  visible,
  onClose,
  country,
  source,
  screen,
  onEmailSubmit,
  mode = 'default',
  curriculumRequestUrl,
}) => {
  const intl = isInternational(country);
  const isAddon = mode === 'curriculum-addon';
  const isQuote = mode === 'quote';
  // Add-on and quote share the same shape: a request-form CTA + "what you'll
  // get" list, no "this requires a plan" framing. Only the headline copy and the
  // form destination differ between them.
  const isRequestFlow = isAddon || isQuote;
  // Where the primary CTA points. A caller-supplied per-curriculum request page
  // (curriculumRequestUrl) always wins — every audience, including a classroom
  // teacher on the default gate, is sent straight to that specific curriculum's
  // request page when one exists. Otherwise each mode falls back to its own
  // generic destination: the /quotes page (quote), the shared curriculum lead
  // Typeform (add-on), or the school-plan interest form (default teacher gate).
  const primaryCtaUrl =
    curriculumRequestUrl ??
    (isQuote ? QUOTES_URL : isAddon ? CURRICULUM_TYPEFORM_URL : INTEREST_FORM_URL);
  const trackCta = usePaywallCtaTracking();
  const ctaCtx = {
    paywallType: 'premium-feature-gate' as const,
    source,
    screen,
  };
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);

  // Synchronously clear the thanks state on every close path. The modal is
  // mounted persistently by `usePaywallPrompt`, so without this the next
  // reopen would render the stale ThanksView instead of the primary view.
  const handleClose = useCallback(() => {
    setSubmittedEmail(null);
    onClose();
  }, [onClose]);

  const handleEmailSubmit = (email: string) => {
    onEmailSubmit?.(email);
    setSubmittedEmail(email);
  };

  if (submittedEmail) {
    return (
      <ModalShell visible={visible} onClose={handleClose} width={480}>
        <ThanksBody>
          <ThanksSticker src="/images/blocked-paywall-stickers/party.svg" alt="" />
          <ThanksHeading>
            <T keyName="BlockedPaywall.OnIt.Heading" defaultValue="We're on it!" />
          </ThanksHeading>
          <ThanksText>
            {isAddon ? (
              <T
                keyName="CurriculumAddon.OnIt.Body"
                defaultValue="We'll be in touch at <strong>{email}</strong> within 1 business day to set up your curriculum-aligned lessons 🙂"
                params={{ email: submittedEmail, strong: <strong /> }}
              />
            ) : (
              <T
                keyName="BlockedPaywall.OnIt.Body"
                defaultValue="We'll be in touch at <strong>{email}</strong> within 1 business day to help get your school on a plan 🙂"
                params={{ email: submittedEmail, strong: <strong /> }}
              />
            )}
          </ThanksText>
          <div style={{ display: 'inline-block' }}>
            <OrangeBtn block={false} onClick={handleClose}>
              <T keyName="BlockedPaywall.OnIt.Back" defaultValue="Back to lesson" />
            </OrangeBtn>
          </div>
        </ThanksBody>
      </ModalShell>
    );
  }

  return (
    <ModalShell visible={visible} onClose={handleClose} width={560}>
      {isQuote ? (
        <StickerHeader
          sticker="rocket"
          tone="orange"
          badgeIcon="crown"
          badge={<T keyName="CurriculumQuote.Badge" defaultValue="Request a quote" />}
          heading={
            <T
              keyName="CurriculumQuote.Heading"
              defaultValue="Bring curriculum and TEKS alignment to your school or district"
            />
          }
        />
      ) : isAddon ? (
        /* An add-on is something to buy, so the badge carries the orange lock;
           the purple gift is reserved for what the free plan already includes. */
        <StickerHeader
          sticker="stars"
          tone="orange"
          badgeIcon="lock"
          badge={<T keyName="CurriculumAddon.Badge" defaultValue="Curriculum add-on" />}
          heading={
            <T
              keyName="CurriculumAddon.Heading"
              defaultValue="Add curriculum and TEKS alignment to your plan"
            />
          }
        />
      ) : (
        <StickerHeader
          sticker="crown"
          tone="lavender"
          badgeIcon="crown"
          badge={<T keyName="PremiumFeature.Badge" defaultValue="School plan only" />}
          heading={
            <T
              keyName="PremiumFeature.Heading"
              defaultValue="This feature requires the school plan"
            />
          }
        />
      )}
      <ModalBody>
        <Heading>
          {isQuote ? (
            <T
              keyName="CurriculumQuote.Sub"
              defaultValue="Get Curipod's AI feedback activities mapped to your scope and sequence across your school or district."
            />
          ) : isAddon ? (
            <T
              keyName="CurriculumAddon.Sub"
              defaultValue="Curriculum alignment and TEKS browsing is an add-on to your school plan. Leave your email or fill out the form and we'll get your curriculum-aligned lessons set up."
            />
          ) : intl ? (
            <T
              keyName="PremiumFeature.Sub.Intl"
              defaultValue="Unlock every School Plan feature for your class."
            />
          ) : (
            <T
              keyName="PremiumFeature.Sub.US"
              defaultValue="We can help you out with every step. You only need to:"
            />
          )}
        </Heading>

        {intl && !isRequestFlow ? (
          <a
            href={PRICING_URL}
            style={{ textDecoration: 'none' }}
            onClick={() => trackCta('see-pricing', ctaCtx)}
          >
            <OrangeBtn>
              <T
                keyName="BlockedPaywall.SeePricing"
                defaultValue="See plans and pricing"
              />
              <ArrowIcon />
            </OrangeBtn>
          </a>
        ) : (
          <>
            <a
              href={primaryCtaUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: 'none' }}
              onClick={() => trackCta('fill-out-form', ctaCtx)}
            >
              <OrangeBtn>
                {isQuote ? (
                  <T keyName="CurriculumQuote.FormCta" defaultValue="Request a quote" />
                ) : isAddon ? (
                  <T
                    keyName="CurriculumAddon.FormCta"
                    defaultValue="Request curriculum alignment"
                  />
                ) : (
                  <T
                    keyName="BlockedPaywall.FillOutFormCta"
                    defaultValue="Fill out this form to get help"
                  />
                )}
              </OrangeBtn>
            </a>
            {/* Admins/buyers submit the quote request only — no email lead
                capture. Teachers and school-plan users keep the email fallback. */}
            {!isQuote && (
              <>
                <OrDivider>
                  <T keyName="BlockedPaywall.Or" defaultValue="or" />
                </OrDivider>
                <EmailForm onSubmit={handleEmailSubmit} />
              </>
            )}
          </>
        )}

        <IncludedPanel>
          <PanelLabel>
            {isRequestFlow ? (
              <T keyName="CurriculumAddon.IncludedLabel" defaultValue="What you'll get" />
            ) : (
              <T
                keyName="PremiumFeature.IncludedLabel"
                defaultValue="The school plan includes"
              />
            )}
          </PanelLabel>
          {(isRequestFlow
            ? [
                <T
                  key="addon-1"
                  keyName="CurriculumAddon.Incl.Mapped"
                  defaultValue="Every unit mapped to your scope and sequence"
                />,
                <T
                  key="addon-2"
                  keyName="CurriculumAddon.Incl.Lessons"
                  defaultValue="Ready-to-teach, STAAR-aligned writing lessons"
                />,
                <T
                  key="addon-3"
                  keyName="CurriculumAddon.Incl.Grading"
                  defaultValue="AI feedback writing practice with every unit"
                />,
              ]
            : [
                <T
                  key="feat-1"
                  keyName="PremiumFeature.Incl.Features"
                  defaultValue="School Plan features"
                />,
                <T
                  key="feat-2"
                  keyName="PremiumFeature.Incl.Sessions"
                  defaultValue="Unlimited Weekly Sessions"
                />,
                <T
                  key="feat-3"
                  keyName="PremiumFeature.Incl.Content"
                  defaultValue="School Plan Content"
                />,
              ]
          ).map((node, i, arr) => (
            <IncludedRow key={i} $last={i === arr.length - 1}>
              <CheckBubble>
                <CheckIcon />
              </CheckBubble>
              <IncludedText>{node}</IncludedText>
            </IncludedRow>
          ))}
        </IncludedPanel>

        <Centered>
          <LinkButton
            href={LEARN_MORE_URL}
            target="_blank"
            onClick={() => trackCta('learn-more', ctaCtx)}
          >
            <T keyName="BlockedPaywall.LearnMore" defaultValue="Learn more" />
          </LinkButton>
        </Centered>
      </ModalBody>
    </ModalShell>
  );
};
